import { autoUpdate, computePosition, flip, offset, shift, VirtualElement } from "@floating-ui/dom";

export type MenuText = string | ((ctx: ContextMenuOpenContext) => string);
export type MenuBool = boolean | ((ctx: ContextMenuOpenContext) => boolean);

export interface ContextMenuOpenContext {
    anchorEvent: MouseEvent;
    target?: HTMLElement;
    payload?: unknown;
}

export interface ContextMenuItem {
    text: MenuText;
    hidden?: MenuBool;
    disabled?: MenuBool;
    onClick?: (ctx: ContextMenuOpenContext) => void | Promise<void>;
    submenu?: ContextMenuItem[];
}

export interface ContextMenuModel {
    items: ContextMenuItem[];
}

interface NormalizedItem {
    label: string;
    disabled: boolean;
    onClick?: (ctx: ContextMenuOpenContext) => void | Promise<void>;
    submenu?: NormalizedItem[];
}

export class ContextMenu
{
    private mainEl: HTMLDivElement | null = null;
    private subEl: HTMLDivElement | null = null;
    private cleanupMain: (() => void) | null = null;
    private cleanupSub: (() => void) | null = null;
    private boundClose?: (e: MouseEvent) => void;

    constructor(private readonly model: ContextMenuModel)
    {}

    open(ctx: ContextMenuOpenContext): void
    {
        this.close();

        this.mainEl = this.createMenuContainer("main");
        document.body.appendChild(this.mainEl);

        const ref = this.createVirtualReference(ctx.anchorEvent);

        this.cleanupMain = autoUpdate(ref, this.mainEl, () =>
        {
            computePosition(ref, this.mainEl, {
                placement: "right-start",
                middleware: [offset(4), flip(), shift()]
            }).then(({ x, y }) =>
            {
                Object.assign(this.mainEl.style, {
                    left: `${x}px`,
                    top: `${y}px`
                });
            });
        });

        const items = this.model.items
            .filter(i => !this.evalBool(i.hidden, ctx))
            .map(i => this.normalize(i, ctx))
            .filter(Boolean);

        items.forEach(i => this.renderRow(this.mainEl, i, ctx));

        this.boundClose = e =>
        {
            if (
                !this.mainEl?.contains(e.target as Node) &&
                !this.subEl?.contains(e.target as Node)
            )
            {
                this.close();
            }
        };

        setTimeout(() => document.addEventListener("mousedown", this.boundClose), 0);
    }

    close(): void
    {
        this.subEl?.remove();
        this.mainEl?.remove();
        this.cleanupSub?.();
        this.cleanupMain?.();

        if (this.boundClose)
        {
            document.removeEventListener("mousedown", this.boundClose);
            this.boundClose = undefined;
        }

        this.subEl = null;
        this.mainEl = null;
        this.cleanupSub = null;
        this.cleanupMain = null;
    }

    destroy(): void
    {
        this.close();
    }

    private createMenuContainer(kind: "main" | "sub"): HTMLDivElement
    {
        const el = document.createElement("div");
        Object.assign(el.style, {
            position: "absolute",
            background: "#fff",
            border: "1px solid #ccc",
            borderRadius: "4px",
            fontSize: "12px",
            zIndex: "999",
            boxShadow: "0 3px 12px rgba(0,0,0,0.20)"
        });
        return el;
    }

    private createVirtualReference(e: MouseEvent): VirtualElement
    {
        return {
            getBoundingClientRect()
            {
                return {
                    x: e.clientX,
                    y: e.clientY,
                    top: e.clientY,
                    left: e.clientX,
                    right: e.clientX,
                    bottom: e.clientY,
                    width: 0,
                    height: 0
                };
            }
        };
    }

    private evalText(v: MenuText, ctx: ContextMenuOpenContext): string
    {
        return typeof v === "function" ? v(ctx) : v;
    }

    private evalBool(v: MenuBool | undefined, ctx: ContextMenuOpenContext): boolean
    {
        return typeof v === "function" ? v(ctx) : !!v;
    }

    private normalize(item: ContextMenuItem, ctx: ContextMenuOpenContext): NormalizedItem | null
    {
        const label = this.evalText(item.text, ctx);
        if (!label)
        {
            return null;
        }

        const submenu = item.submenu
            ?.filter(s => !this.evalBool(s.hidden, ctx))
            .map(s => this.normalize(s, ctx))
            .filter(Boolean);

        return {
            label,
            disabled: this.evalBool(item.disabled, ctx),
            onClick: item.onClick,
            submenu
        };
    }

    private renderRow(parent: HTMLElement, item: NormalizedItem, ctx: ContextMenuOpenContext): void
    {
        const row = document.createElement("div");
        row.style.padding = "3px 5px";
        row.style.display = "flex";
        row.style.justifyContent = "space-between";
        row.style.cursor = item.disabled ? "default" : "pointer";

        const label = document.createElement("span");
        label.textContent = item.label;
        row.appendChild(label);

        if (item.submenu?.length)
        {
            const arrow = document.createElement("span");
            arrow.textContent = "►";
            row.appendChild(arrow);
        }

        if (item.disabled)
        {
            row.style.color = "#aaa";
        }
        else
        {
            row.addEventListener("mouseenter", () =>
            {
                row.style.background = "#eee";
                if (item.submenu)
                {
                    this.openSubmenu(row, item.submenu, ctx);
                }
            });

            row.addEventListener("mouseleave", () => (row.style.background = ""));

            row.addEventListener("click", async () =>
            {
                if (!item.submenu)
                {
                    this.close();
                    await item.onClick?.(ctx);
                }
            });
        }

        parent.appendChild(row);
    }

    private openSubmenu(
        anchor: HTMLElement,
        items: NormalizedItem[],
        ctx: ContextMenuOpenContext
    ): void
    {
        this.closeSubmenu();

        this.subEl = this.createMenuContainer("sub");
        document.body.appendChild(this.subEl);

        items.forEach(i => this.renderRow(this.subEl, i, ctx));

        this.subEl.addEventListener("mouseenter", () =>
        {
            // prevents premature close
        });

        this.subEl.addEventListener("mouseleave", () =>
        {
            const hovered = document.querySelector(":hover");
            if (!anchor.contains(hovered))
            {
                this.closeSubmenu();
            }
        });

        this.cleanupSub = autoUpdate(anchor, this.subEl, () =>
        {
            computePosition(anchor, this.subEl, {
                placement: "right-start",
                middleware: [offset(4), flip(), shift()]
            }).then(({ x, y }) =>
            {
                Object.assign(this.subEl.style, {
                    left: `${x}px`,
                    top: `${y}px`
                });
            });
        });
    }

    private closeSubmenu(): void
    {
        this.subEl?.remove();
        this.cleanupSub?.();
        this.subEl = null;
        this.cleanupSub = null;
    }
}
